import { formatPrice } from 'lib/utils/formatters';
import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Spinner } from 'reactstrap';
import profile_placeholder from 'assets/images/profile_placeholder.png';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { makePaymentAndApproveInspection } from 'endpoints/builder/builderPropertyListingDetails';
import { toast } from 'react-toastify';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  toggle: () => void;
  inspectionDetails?: any;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({ isOpen, toggle, inspectionDetails }) => {
  const queryClient = useQueryClient();
  const makePaymentMutation = useMutation({
    mutationFn: makePaymentAndApproveInspection,
    onSuccess: (data) => {
      if (data?.status_code === 201) {
        if(data?.data?.inspectionType === 'primary'){
          queryClient.invalidateQueries({ queryKey: ['propertyUnitInspectionDetails'] });
        }else{
          queryClient.invalidateQueries({ queryKey: ['propertyUnitSecondaryInspectionDetails'] });
        }
        toast.success(data?.response || 'Payment successful');
        toggle();
      }
      if (data?.status_code === 409) {
        toast.error(data?.response || 'Payment failed');
      }
    },
    onError: (_error) => {
      toast.error('An error occurred during payment. Please try again.');
    },
  });

  const handleMakePayment = () => {
    if (inspectionDetails) {
      makePaymentMutation.mutate({
        inspectionId: inspectionDetails?.id,
        inspectionAmount: inspectionDetails?.inspection_amount,
        paymentMethod: 'internal_transfer'
      });
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} className="fw-bold border-0 pb-0">
        Transaction Details
      </ModalHeader>

      <ModalBody className="pt-3 pb-4 px-4">
        {/* Divider line */}
        <hr className="mt-0 mb-3" style={{ borderColor: '#c3c5c7ff' }} />

        {/* Payment and Property Details Section */}
        <Row className="mb-3">
          <Col xs={12}>
            <div className="fw-bold" >Payment</div>
          </Col>
        </Row>

        {/* Purpose and Property Details Section */}
        <Row className="mb-4">
          <Col xs={6}>
            <div>
              <span className="fw-bold mb-1">Purpose</span>
              <div className='mt-1'>Approve Inspection Report</div>
            </div>
          </Col>
          <Col xs={6} className="text-end">
            <div className="fw-bold mb-1" >Property Details</div>
            <div className="text-end">
              <div>{inspectionDetails?.property?.property_address || 'N/A'}</div>
            </div>
          </Col>
        </Row>

        {/* Details Card */}
        <div className="border rounded-3" >
          {/* Status Row */}
          <div
            className="d-flex justify-content-between align-items-center px-3 py-3"
            style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem 0.375rem 0 0' }}
          >
            <span className="fw-bold" style={{ fontSize: '0.95rem' }}>Details</span>
            {/* <span 
              className="badge rounded-pill bg-success" 
            >
              Pending
            </span> */}
          </div>

          {/* Amount Due Row */}
          <div className="d-flex justify-content-between align-items-center px-3 py-3 border-top">
            <span className="fw-bold" style={{ fontSize: '0.95rem' }}>Amount Due</span>
            <span style={{ fontSize: '0.95rem', fontWeight: '500' }}>{`$${formatPrice(inspectionDetails?.inspection_amount)}`}</span>
          </div>

          {/* Inspection Type Row */}
          <div className="d-flex justify-content-between align-items-center px-3 py-3 border-top">
            <span className="fw-bold" style={{ fontSize: '0.95rem' }}>Inspection Type</span>
            <span style={{ fontSize: '0.95rem' }}>Company Inspection</span>
          </div>

          {/* Payment Method Row */}
          <div className="d-flex justify-content-between align-items-center px-3 py-3 border-top">
            <span className="fw-bold" style={{ fontSize: '0.95rem' }}>Payment Method</span>
            <span style={{ fontSize: '0.95rem' }}>Internal Transfer</span>
          </div>

          {/* Inspector Row */}
          <div className="px-3 py-3 border-top">
            <div className="fw-bold mb-3" style={{ fontSize: '0.95rem' }}>Inspector</div>
            <div
              className="border rounded-3 d-flex align-items-center p-2"
              style={{ backgroundColor: 'white' }}
            >
              <div
                className="rounded-circle bg-secondary d-flex justify-content-center align-items-center me-3"
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#c4c4c4 !important'
                }}
              >
                <img src={inspectionDetails?.inspector?.profile_image || profile_placeholder} alt='Inspector profile' className="rounded-circle" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <span className="flex-grow-1 fw-medium" style={{ fontSize: '0.95rem' }}>
                {`${inspectionDetails?.inspector?.first_name || 'N/A'} ${inspectionDetails?.inspector?.last_name || 'N/A'}`}
              </span>
              {inspectionDetails?.inspector?.phone_no && <button
                className="btn btn-link p-0 text-dark"
                style={{ fontSize: '1.2rem' }}
                onClick={(e) => e.preventDefault()}
              >
                <i className="ri-phone-line"></i>
              </button>}
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="border-0 pt-0">
        <Button
          color="danger"
          onClick={toggle}
          className="me-2 px-4"
        >
          Cancel
        </Button>
        <Button
          color="success"
          onClick={handleMakePayment}
          className="px-4"
          disabled={makePaymentMutation.isPending}
        >
          {makePaymentMutation.isPending
            ? (<><Spinner size="sm" className='me-2' />Loading...</>)
            : 'Make Payment'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TransactionDetailsModal;